import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IPolicyContentDto, IPolicyDto } from '@app/interfaces/policy.interface';
import { IResponseItem } from '@app/interfaces/response-item.interface';
import { PolicyModel } from '@app/models/policy.model';
import { TPolicyType } from '@app/types/policy.type';
import { POLICY_SCOPE_ALL } from '@app/utils/constants';
import { tools } from '@app/utils/tools';
import { translations } from '@app/utils/translations';
import { environment } from '@env/environment';
import { catchError, map, Observable, of, throwError } from 'rxjs';

const ENDPOINT = environment.api + '/policies/';

@Injectable({
    providedIn: 'root',
})
export class PoliciesHttpService {
    private http: HttpClient = inject(HttpClient);

    get(): Observable<PolicyModel[]> {
        return this.http
            .get<IPolicyDto[]>(ENDPOINT)
            .pipe(map(policies => policies.map(policy => new PolicyModel(policy))));
    }

    getById(id: string): Observable<PolicyModel> {
        return this.http
            .get<IPolicyDto>(ENDPOINT + id)
            .pipe(map(policy => new PolicyModel(policy)));
    }

    create(policy: PolicyModel): Observable<IResponseItem<PolicyModel>> {
        return this.http.post<IPolicyDto>(ENDPOINT, policy.dataTransferObject()).pipe(
            map(policy => ({
                value: new PolicyModel(policy),
            })),
            catchError(error =>
                of({
                    error: {
                        error: true,
                        message: tools.getErrorTranslation(
                            error,
                            translations.management.policies.error.create,
                        ),
                    },
                }),
            ),
        );
    }

    patch(policy: PolicyModel): Observable<IResponseItem<PolicyModel>> {
        return this.http
            .patch<IPolicyDto>(ENDPOINT + policy.id, policy.patchDataTransferObject())
            .pipe(
                map(policy => ({
                    value: new PolicyModel(policy),
                })),
                catchError(error =>
                    of({
                        error: {
                            error: true,
                            message: tools.getErrorTranslation(
                                error,
                                translations.management.policies.error.update,
                            ),
                        },
                    }),
                ),
            );
    }

    delete(id: string): Observable<IResponseItem<PolicyModel>> {
        return this.http.delete(ENDPOINT + id).pipe(
            map(() => ({
                success: true,
            })),
            catchError(error =>
                of({
                    error: {
                        error: true,
                        message: tools.getErrorTranslation(
                            error,
                            translations.management.policies.delete.error,
                        ),
                    },
                }),
            ),
        );
    }

    publish(id: string): Observable<IResponseItem<PolicyModel>> {
        return this.http.post<IPolicyDto>(ENDPOINT + id + '/publish', null).pipe(
            map(policy => ({
                success: true,
                value: new PolicyModel(policy),
            })),
            catchError(error =>
                of({
                    error: {
                        error: true,
                        message: tools.getErrorTranslation(
                            error,
                            translations.management.policies.publish.error,
                        ),
                    },
                }),
            ),
        );
    }

    /** Published content for everyone; emits `null` while nothing of this type is published. */
    getContent(type: TPolicyType, language: string): Observable<IPolicyContentDto | null> {
        return this.http
            .get<IPolicyContentDto>(ENDPOINT + 'content/' + type, {
                params: { lang: language, scope: POLICY_SCOPE_ALL },
            })
            .pipe(
                catchError((error: HttpErrorResponse) =>
                    error.status === HttpStatusCode.NotFound ? of(null) : throwError(() => error),
                ),
            );
    }
}
